import * as React from 'react';
import { FormattedNumber } from 'react-intl';

import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import { ILocale } from '@containers/App/model';
import { useIsDualPriceForCroatia } from '@containers/ProductDetailContainer/ProductDetail/hooks/useIsDualPriceForCroatia';
import { useNewPdDesignEnabled } from '@containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';
import { usePriceLogic } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic';
import { getDiscountPercentage } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic/utils';

import { Discount, DiscountWrapper, LineThrough, RrpSlash } from './styled';

interface IVariantDiscountProps {
  variant: CatalogVariantFragmentFragment;
  locale: ILocale;
}

export const VariantDiscount: React.FC<IVariantDiscountProps> = ({
  variant,
  locale,
}) => {
  const { topPrice } = usePriceLogic(variant);
  const newDesign = useNewPdDesignEnabled();
  const {
    isDualPriceForCroatia,
    rightSideCurrencyCode,
    leftSideCurrency,
    getRightSidePrice,
    getLeftSideFormattedPrice,
  } = useIsDualPriceForCroatia();

  if (!topPrice) {
    return null;
  }

  return (
    <DiscountWrapper>
      <Discount newDesign={newDesign}>
        {-getDiscountPercentage(variant.price.value, topPrice.value.value)}%
      </Discount>

      {isDualPriceForCroatia && (
        <>
          <LineThrough
            newDesign={newDesign}
            active={topPrice.isCrossed}
            data-testid="originalDualPriceLineThroughWrapper"
          >
            {getLeftSideFormattedPrice(topPrice.value.value)} {leftSideCurrency}
          </LineThrough>
          <RrpSlash newDesign={newDesign}>/</RrpSlash>
        </>
      )}

      <LineThrough
        removeLeftPadding={isDualPriceForCroatia}
        newDesign={newDesign}
        active={topPrice.isCrossed}
        data-testid="originalPriceLineThroughWrapper"
      >
        <FormattedNumber
          value={
            isDualPriceForCroatia
              ? getRightSidePrice(topPrice.value.value)
              : topPrice.value.value
          }
          currency={
            isDualPriceForCroatia
              ? rightSideCurrencyCode
              : topPrice.value.currency
          }
          currencyDisplay="symbol"
          style="currency"
          minimumFractionDigits={locale.priceDecimalPlaces}
        />
      </LineThrough>
    </DiscountWrapper>
  );
};
